import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Clock, ArrowRight, X } from 'lucide-react';
import Button from './Button';
import { useAuthStore } from '../../store/authStore';

export default function TrialBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [dismissed, setDismissed] = useState(false);

  const subscription = user?.subscription;
  if (!subscription) return null;

  // A trialing subscription with a payment provider (Stripe) means the user has
  // already subscribed (card on file) — don't nag them with the free-trial /
  // "choose plan" banner. Only treat as a free trial when there's no provider.
  const isTrialing = subscription.status === 'trialing' && !subscription.provider;
  const isFreeAfterTrial =
    subscription.plan === 'free' &&
    subscription.status === 'active' &&
    subscription.currentPeriodStart;

  // Nothing to show if not trialing and not recently downgraded
  if (!isTrialing && !isFreeAfterTrial) return null;

  // Calculate days left for trial
  let daysLeft = 0;
  if (isTrialing && subscription.currentPeriodEnd) {
    const end = new Date(subscription.currentPeriodEnd).getTime();
    daysLeft = Math.max(0, Math.ceil((end - Date.now()) / (1000 * 60 * 60 * 24)));
  }

  const handleChoosePlan = () => {
    navigate('/pricing');
  };

  // Expired trial: persistent inline banner — sits directly under the topbar
  // inside the main column, so it scopes correctly with sidebar layouts.
  if (isFreeAfterTrial) {
    return (
      <div
        role="alert"
        className="bg-amber-500/95 border-b border-amber-600"
      >
        <div className="px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-sm font-medium text-amber-950">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span>{t('trial.bannerExpired')}</span>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={handleChoosePlan}
            className="gap-1 text-xs flex-shrink-0"
          >
            {t('trial.selectPlan')}
            <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      </div>
    );
  }

  // Trial active banner (dismissible)
  if (dismissed) return null;

  return (
    <div className="bg-accent-primary/10 border-b border-accent-primary/20">
      <div className="container mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-text-primary">
          <Clock className="w-4 h-4 text-accent-primary flex-shrink-0" />
          <span>{t('trial.banner', { days: daysLeft })}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={handleChoosePlan}
            className="gap-1 text-xs"
          >
            {t('trial.choosePlan')}
            <ArrowRight className="w-3 h-3" />
          </Button>
          <button
            onClick={() => setDismissed(true)}
            className="text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
